import * as R from 'ramda';
import Pusher from 'pusher-js';
import Echo from 'laravel-echo';
import {Transport} from 'pusher-js/types/src/core/config';

type WebsocketConfig = {
  pusherKey: string;
  pusherCluster: string;
  pusherHost: string;
  authEndpoint: string;
};

export function getWebsocket({
  pusherKey,
  pusherCluster,
  pusherHost,
  authEndpoint,
}: WebsocketConfig): Echo {
  const endpointConfig = R.isEmpty(pusherCluster)
    ? {
        wsHost: pusherHost,
        disableStats: true,
        enabledTransports: ['ws', 'wss'] as Transport[],
      }
    : {
        cluster: pusherCluster,
      };

  return new Echo({
    broadcaster: 'pusher',
    key: pusherKey,
    encrypted: true,
    client: new Pusher(pusherKey, {
      authEndpoint,
      ...endpointConfig,
    }),
  });
}

export async function getAuthenticatedWebSocket({
  pusherKey,
  pusherCluster,
  pusherHost,
  authEndpoint,
  token,
}: WebsocketConfig & {
  token: string;
}): Promise<Echo> {
  const commonConfig = {
    broadcaster: 'pusher',
    encrypted: true,
    authEndpoint,
    auth: {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
    key: pusherKey,
  };

  // If the cluster is empty, assume we're connecting to the local endpoint (Socketi)
  const endpointConfig = R.isEmpty(pusherCluster)
    ? {
        wsHost: pusherHost,
        disableStats: true,
        enabledTransports: ['ws', 'wss'],
      }
    : {
        cluster: pusherCluster,
      };

  return new Echo({
    ...commonConfig,
    ...endpointConfig,
  });
}

type GetSocketIdSuccessResponse = {
  status: 'success';
  socketId: string;
};
type GetSocketIdTimeoutResponse = {
  status: 'error';
  errorType: 'timeout';
};
export const getSocketId = (
  websocket: Echo,
  timeoutMs = 3000
): Promise<GetSocketIdSuccessResponse | GetSocketIdTimeoutResponse> => {
  const websocketPromise = new Promise<GetSocketIdSuccessResponse>(resolve => {
    const interval = setInterval(() => {
      const socketId = websocket.socketId();
      if (socketId) {
        clearInterval(interval);
        resolve({
          status: 'success',
          socketId,
        });
      }
    }, 200);
  });

  return process.env.NODE_ENV === 'test'
    ? websocketPromise
    : Promise.race([
        websocketPromise,
        new Promise<GetSocketIdTimeoutResponse>(resolve =>
          setTimeout(
            () =>
              resolve({
                status: 'error',
                errorType: 'timeout',
              }),
            timeoutMs
          )
        ),
      ]);
};
